/**
 * The one number format of the statistics pages, identical to PHP
 * number_format($value, $decimals, ',', ' '): thousands are always grouped with
 * a space (four-digit numbers too: „9 543”), a comma separates a fixed number
 * of decimals and a negative value takes the typographic minus.
 *
 * Intl.NumberFormat('pl-PL') leaves four-digit numbers ungrouped („9543”), so
 * values recomputed in the browser after a filter change would not match the
 * ones the server printed; every report script formats through this helper.
 */
export function formatNumber(value, decimals = 0) {
    const number = Number(value);
    if (value === null || value === undefined || value === '' || !Number.isFinite(number)) {
        return '';
    }
    const fixed = Math.abs(number).toFixed(decimals);
    const [integer, fraction] = fixed.split('.');
    const grouped = integer.replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
    const negative = number < 0 && Number(fixed) !== 0;
    return (negative ? '−' : '') + grouped + (fraction ? ',' + fraction : '');
}

/**
 * PHP's round($value, $decimals) of PHP 8.4: half away from zero, with the
 * half measured against the decimal edge as PHP measures it, so 9,995 rounds
 * to 10 and 1,005 to 1,01, where Math.round(value * 100) / 100 gives 9,99 and
 * 1 (the double nearest 1,005 lies below it, and the product rounds down).
 * The server rounds prices with it (TransakcjaFormatter::roundPricePerM2(),
 * ValuationReportService::buildPriceBlock()): a number recomputed here from the
 * same inputs matches the one the server printed. A port of _php_math_round().
 */
export function roundDecimal(value, decimals = 0) {
    const number = Number(value);
    if (!Number.isFinite(number) || number === 0) {
        return number;
    }
    const exponent = 10 ** Math.abs(decimals);
    const magnitude = Math.abs(number);
    let integral = Math.floor(decimals > 0 ? magnitude * exponent : magnitude / exponent);
    // A product that falls just short of a whole number (0,29 × 100 = 28,999…) is that number.
    if ((decimals > 0 ? (integral + 1) / exponent : (integral + 1) * exponent) === magnitude) {
        integral += 1;
    }
    if (integral >= 1e16) {
        return number;
    }
    if (magnitude >= (decimals > 0 ? (integral + 0.5) / exponent : (integral + 0.5) * exponent)) {
        integral += 1;
    }
    const rounded = decimals > 0 ? integral / exponent : integral * exponent;

    return number < 0 ? -rounded : rounded;
}

/**
 * Below this price per square metre (cheap farmland, 5,59 zł/m²) whole złoty
 * would hide most of the value. PHP twin:
 * TransakcjaFormatter::PRICE_PER_M2_DECIMALS_BELOW.
 */
export const PRICE_PER_M2_DECIMALS_BELOW = 10;

/** Decimals of a price per square metre: two below PRICE_PER_M2_DECIMALS_BELOW, none from it. */
function pricePerM2Decimals(number) {
    return number !== 0 && roundDecimal(Math.abs(number), 2) < PRICE_PER_M2_DECIMALS_BELOW ? 2 : 0;
}

/**
 * The number formatPricePerM2() shows, for differences and bars: PHP twin
 * TransakcjaFormatter::roundPricePerM2() (whole złoty, two decimals below 10 zł).
 */
export function roundPricePerM2(value) {
    const number = Number(value);

    return Number.isFinite(number) ? roundDecimal(number, pricePerM2Decimals(number)) : number;
}

/**
 * A price per square metre like TransakcjaFormatter::formatPricePerM2(): whole
 * złoty („5 809”), two decimals below PRICE_PER_M2_DECIMALS_BELOW („5,59”);
 * 9,996 and 9,995 round to „10”, 5,595 to „5,60”, as PHP rounds them.
 */
export function formatPricePerM2(value) {
    const number = Number(value);
    if (value === null || value === undefined || value === '' || !Number.isFinite(number)) {
        return '';
    }

    return formatNumber(roundPricePerM2(number), pricePerM2Decimals(number));
}
